import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowDown, ArrowUp, Download, Minus, X } from "lucide-react";

import type { ParcelInfo } from "../types/deforestation";
import type {
  ClassCoverage,
  ParcelStats,
} from "../utils/parcel-stats";
import { formatHa, formatPct } from "../utils/format";

interface ParcelDetailPanelProps {
  parcel: ParcelInfo;
  stats: ParcelStats | null;
  onExport: () => void;
  onClose: () => void;
}

// Forest crown-cover palette — must stay in sync with the PNG palette baked
// by `tile-forest-raster.py`.
const FOREST_CC_COLORS: Record<string, string> = {
  "Closed Forest": "#006400",
  "Open Forest": "#32CD32",
  Woodland: "#228B22",
  "Unknown Forest": "#7AAE7A",
  "Non-forest": "#d4d4d8",
};

// SatSure LULC palette — matches LULC_COLORS in agricultural-lulc/config.
const LULC_COLORS: Record<string, string> = {
  Waterbody: "#003fbf",
  "Built Up": "#e75a5a",
  "Pasture/Fallow": "#6ee16e",
  "Crop Land": "#fdf06f",
  "Barren Land": "#fdae61",
  "Plantation/Trees": "#01896e",
  "Range Land": "#3dbf35",
  Forest: "#018933",
};

// QLD RVM categories carry their own colour cue: protected (B/C/R/A)
// stay green-ish, X (clearable) is amber, Water blue.
const RVM_COLORS: Record<string, string> = {
  A: "#84cc16",
  B: "#15803d",
  C: "#22c55e",
  R: "#86efac",
  X: "#f59e0b",
  Water: "#3b82f6",
};

function lotPlanLine(parcel: ParcelInfo): string {
  const parts: string[] = [];
  if (parcel.lot) parts.push(`Lot ${parcel.lot}`);
  if (parcel.plan) parts.push(`on ${parcel.plan}`);
  if (parcel.state) parts.push(`— ${parcel.state}`);
  return parts.join(" ");
}

function ChangeIndicator({ pct }: { pct: number }) {
  const rounded = Math.round(pct * 100) / 100;
  if (rounded === 0)
    return <Minus className="size-3 text-muted-foreground inline" />;
  if (rounded > 0)
    return (
      <span className="inline-flex items-center text-xs text-emerald-600">
        <ArrowUp className="size-3" />
        {rounded.toFixed(2)}%
      </span>
    );
  return (
    <span className="inline-flex items-center text-xs text-red-500">
      <ArrowDown className="size-3" />
      {Math.abs(rounded).toFixed(2)}%
    </span>
  );
}

interface ForestRow {
  class: string;
  pct_2020: number;
  pct_2025: number;
}

function buildForestRows(
  forest2020: ClassCoverage[] | undefined,
  forest2025: ClassCoverage[] | undefined,
): ForestRow[] {
  const classes = new Set<string>([
    ...(forest2020 ?? []).map((r) => r.class),
    ...(forest2025 ?? []).map((r) => r.class),
  ]);
  return Array.from(classes)
    // Non-forest is the inverse of tree cover (already shown by the Tree
    // Cover card); a green up-arrow on rising Non-forest would invert the
    // deforestation semantics, so leave it out of this change-coloured table.
    .filter((cls) => cls !== "Non-forest")
    .map((cls) => ({
      class: cls,
      pct_2020: forest2020?.find((r) => r.class === cls)?.coverage_pct ?? 0,
      pct_2025: forest2025?.find((r) => r.class === cls)?.coverage_pct ?? 0,
    }))
    .sort((a, b) => b.pct_2025 - a.pct_2025);
}

function treeCoverPct(rows: ClassCoverage[] | undefined): number {
  if (!rows) return 0;
  // Sum forest classes (1-4); exclude Non-forest.
  return rows
    .filter((r) => r.class !== "Non-forest")
    .reduce((sum, r) => sum + r.coverage_pct, 0);
}

function DensityBody({
  density2020,
  density2025,
}: {
  density2020: number | undefined;
  density2025: number | undefined;
}) {
  if (density2020 === undefined || density2025 === undefined) {
    return <p className="text-xs text-muted-foreground">No data.</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
      <div>
        <p className="text-[10px] text-muted-foreground uppercase">2020</p>
        <p className="font-medium">{formatPct(density2020)}</p>
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase">2025</p>
        <p className="font-medium">{formatPct(density2025)}</p>
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase">Δ</p>
        <p className="font-medium">
          <ChangeIndicator pct={density2025 - density2020} />
        </p>
      </div>
      <p className="col-span-3 text-[10px] text-muted-foreground">
        Mean crown cover across forest pixels only.
      </p>
    </div>
  );
}

export function ParcelDetailPanel({
  parcel,
  stats,
  onExport,
  onClose,
}: ParcelDetailPanelProps) {
  const subline = lotPlanLine(parcel);
  const forestRows = buildForestRows(stats?.forest2020, stats?.forest2025);
  const treeCover2020 = treeCoverPct(stats?.forest2020);
  const treeCover2025 = treeCoverPct(stats?.forest2025);
  // Authoritative tree density (forest pixels only) — same source the group
  // panel uses, so a parcel reads one consistent density number everywhere.
  const density2020 = stats?.treeDensity2020;
  const density2025 = stats?.treeDensity2025;

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <h2 className="text-xl font-bold truncate">{parcel.parcel_id}</h2>
          {subline && (
            <p className="text-muted-foreground text-sm">{subline}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Parcel Details</h3>
        <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Area</p>
            <p className="font-medium">
              {parcel.area_ha > 0 ? `${formatHa(parcel.area_ha)} Ha` : "—"}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Type</p>
            <p className="font-medium">{parcel.parcelType || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Title</p>
            <p className="font-medium">{parcel.titleStatus || "—"}</p>
          </div>
          <div className="col-span-3">
            <p className="text-[10px] text-muted-foreground uppercase">
              Cadastre PID
            </p>
            <p className="font-mono text-xs break-all">{parcel.cadastrePid}</p>
          </div>
        </div>
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Planning</h3>
        {parcel.planning ? (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Zone</p>
              <p className="font-medium">{parcel.planning.zoneCode ?? "—"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] text-muted-foreground uppercase">
                Description
              </p>
              <p className="font-medium capitalize">
                {(
                  parcel.planning.psmaDescription ??
                  parcel.planning.primaryZoneDescription ??
                  "—"
                ).toLowerCase()}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No planning bundle attached to this parcel.
          </p>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Local Government Area</h3>
        {parcel.lga ? (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div className="col-span-2">
              <p className="text-[10px] text-muted-foreground uppercase">Name</p>
              <p className="font-medium">{parcel.lga.name}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">State</p>
              <p className="font-medium">{parcel.lga.state || "—"}</p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No LGA polygon overlaps this parcel in the current view.
          </p>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Property</h3>
        {parcel.property ? (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Area</p>
              <p className="font-medium">
                {parcel.property.area_ha > 0
                  ? `${formatHa(parcel.property.area_ha)} Ha`
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">State</p>
              <p className="font-medium">{parcel.property.state || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">
                Parcels
              </p>
              <p className="font-medium">
                {parcel.property.cadastrePidsCount}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No property polygon overlaps this parcel in the current view.
          </p>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Tree Cover</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : forestRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">No data.</p>
        ) : (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">2020</p>
              <p className="font-medium">{treeCover2020.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">2025</p>
              <p className="font-medium">{treeCover2025.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Δ</p>
              <p className="font-medium">
                <ChangeIndicator pct={treeCover2025 - treeCover2020} />
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Density</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <DensityBody density2020={density2020} density2025={density2025} />
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Forest Classification</h3>
          <Badge variant="outline" className="text-[10px]">
            Demo zonal histogram
          </Badge>
        </div>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : forestRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No forest data available for this parcel.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Class</TableHead>
                <TableHead className="text-xs text-right">2020</TableHead>
                <TableHead className="text-xs text-right">2025</TableHead>
                <TableHead className="text-xs text-right">Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forestRows.map((row) => (
                <TableRow key={row.class}>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="size-2.5 rounded-sm shrink-0"
                        style={{
                          backgroundColor:
                            FOREST_CC_COLORS[row.class] ?? "#a1a1aa",
                        }}
                      />
                      {row.class}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.pct_2020.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.pct_2025.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right">
                    <ChangeIndicator pct={row.pct_2025 - row.pct_2020} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Land Use (2025)</h3>
          <Badge variant="outline" className="text-[10px]">
            Demo zonal histogram
          </Badge>
        </div>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : !stats.lulc2025 || stats.lulc2025.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No SatSure LULC available.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Class</TableHead>
                <TableHead className="text-xs text-right">Coverage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.lulc2025.map((row) => (
                <TableRow key={row.class}>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="size-2.5 rounded-sm shrink-0"
                        style={{
                          backgroundColor: LULC_COLORS[row.class] ?? "#a1a1aa",
                        }}
                      />
                      {row.class}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.coverage_pct.toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {stats?.nvis && (
          <div className="mt-3 pt-2 border-t border-border text-xs">
            <span className="text-muted-foreground">NVIS native veg: </span>
            <span className="font-medium">{stats.nvis.class}</span>
            <span className="text-muted-foreground">
              {" "}
              ({stats.nvis.coverage_pct.toFixed(1)}%)
            </span>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Regulatory Overlaps</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-xs text-muted-foreground block mb-1">
                QLD Regulated Vegetation
              </span>
              {stats.regulatedVeg && stats.regulatedVeg.length > 0 ? (
                <Table>
                  <TableBody>
                    {stats.regulatedVeg.map((row) => {
                      // Category labels start with "X — …"; pull the first
                      // letter as the swatch key.
                      const code = row.category.split(" ")[0];
                      return (
                        <TableRow key={row.category}>
                          <TableCell className="text-xs">
                            <div className="flex items-center gap-1.5">
                              <div
                                className="size-2.5 rounded-sm shrink-0"
                                style={{
                                  backgroundColor:
                                    RVM_COLORS[code] ?? "#a1a1aa",
                                }}
                              />
                              {row.category}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-right">
                            {row.coverage_pct.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <span className="text-xs text-muted-foreground">
                  No regulated vegetation overlap.
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Koala Habitat
              </span>
              <span className="font-medium">
                {stats.koalaHabitatPct !== undefined
                  ? `${stats.koalaHabitatPct.toFixed(1)}%`
                  : "—"}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Bushfire History</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : stats.bushfires && stats.bushfires.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Year</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs text-right">Area (ha)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.bushfires.map((f, i) => (
                <TableRow key={`${f.year ?? "?"}-${f.type ?? "?"}-${i}`}>
                  <TableCell className="text-xs">
                    {f.year ? (
                      <Badge variant="outline" className="text-[10px]">
                        {f.year}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{f.type ?? "—"}</TableCell>
                  <TableCell className="text-xs text-right">
                    {f.area_ha !== undefined ? formatHa(f.area_ha) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-xs text-muted-foreground">No fire scars recorded.</p>
        )}
      </div>
    </div>
  );
}
